import { calculateRemainingMowerCapacity } from "../../project/coverageCalculations";
import { FinancingOption } from "../../project/models/Enums";
import Project from "../../project/models/Project";
import { DataLayerEvent } from "./tracking";

function calculateTotalPrice(project: Project): number {
  if (!project.proposal) {
    return 0;
  }

  if (project.proposal.financingOption === FinancingOption.Leasing) {
    return project.proposal.leasingCost ?? 0;
  }

  if (project.proposal.mowers == null) {
    return 0;
  }
  let proposalProducts = project.proposal.mowers;
  if (project.proposal.referenceStations?.length) {
    proposalProducts = [...proposalProducts, ...project.proposal.referenceStations];
  }

  return proposalProducts.reduce(
    (a, b) => a + (b.product?.price.valueVatExcl ?? 0) * b.quantity,
    0
  );
}

function getDiscount(project: Project): number | undefined {
  if (project.proposal.financingOption === FinancingOption.DirectPayment) {
    return project.proposal.discount ?? 0;
  }
  return undefined;
}

function getDiscountUnit(project: Project): string | undefined {
  if (project.proposal.financingOption === FinancingOption.DirectPayment) {
    return project.proposal.discountUnit;
  }
  return undefined;
}

function getInstallationMaterialAmount(project: Project): number {
  return (
    (project.proposal.installationMaterialCost ?? 0) +
    (project.proposal.installationLabourCost ?? 0)
  );
}

/**
 * Same mower coverage breakdown as used for PPT export tracking (GTM).
 */
export function getMowerInfoForExportTracking(project: Project) {
  if (!project.proposal?.mowers?.length) return [];

  return project.proposal.mowers.map((product) => {
    const connectedAreas = project.areas.filter((a) =>
      product.areaIds.includes(a.id)
    );

    const firstOrDefaultConnection = connectedAreas.find((a) =>
      a.proposalProductConnections.find(
        (conn) =>
          conn.proposalProduct !== null && conn.proposalProduct.id === product.id
      )
    )?.proposalProductConnections?.[0];

    const possibleToMowOnDifferentHours =
      firstOrDefaultConnection?.possibleToMowOnDifferentHours ?? true;

    return {
      product: product.id,
      productName: product.product.name,
      coverage: calculateRemainingMowerCapacity(
        product.product,
        product.quantity,
        connectedAreas,
        possibleToMowOnDifferentHours,
        product
      ),
    };
  });
}

/**
 * Shared `exportProposal` dataLayer payload for PPT and installation JSON exports.
 */
export function buildExportProposalDataLayerEvent(
  project: Project,
  document: { documentname: string; documenttype: string }
): DataLayerEvent {
  return {
    event: "exportProposal",
    projectInfo: {
      projectId: `${project.id}`,
      projectName: `${project.name}`,
      projectType: `${project.type}`,
      companyName: `${project.customerCompanyName}`,
      country: `${project.region?.name}`,
      usingCurrentSetup: `${project.useCurrentSetup}`,
      paymentMethod: `${project.proposal.financingOption}`,
      totaltAmount: calculateTotalPrice(project),
      currency: `${project.region.currency}`,
      installationAmount: getInstallationMaterialAmount(project),
      discountAmount: getDiscount(project),
      discountUnit: getDiscountUnit(project),
      creator: `${project.createdBy}`,
      hasAdjustedPrices: [
        ...(project.proposal.mowers ?? []),
        ...(project.proposal.referenceStations ?? []),
      ].some((p) => p.adjustedPrice != null),
    },
    mowerInfo: getMowerInfoForExportTracking(project),
    exportProposalInfo: {
      projectId: project.id,
      projectName: project.name,
      documentname: document.documentname,
      documenttype: document.documenttype,
    },
  };
}
